import { action, computed, makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"

import type { SwapAggregatorDAO, SwapFallbackMode } from "./types"
import { migrateLegacyAggregatorIds } from "./migrate-legacy-aggregator-ids"
import { SwapAggregatorFactory } from "./swap-aggregator-factory"
import { SwapAggregatorStore } from "./swap-aggregator-store"

const FALLBACK_AGGREGATOR_KEY = "swap-fallback-aggregator-id"
const FALLBACK_MODE_KEY = "swap-fallback-mode"
const DEFAULT_FALLBACK_AGGREGATOR_ID = "kyber"
const DEFAULT_FALLBACK_MODE: SwapFallbackMode = "multi"

export class SwapAggregatorRepository {
  private aggregators = new Map<string, SwapAggregatorStore>()
  private fallbackAggregatorId: string = DEFAULT_FALLBACK_AGGREGATOR_ID
  private fallbackModeValue: SwapFallbackMode = DEFAULT_FALLBACK_MODE

  constructor() {
    makeObservable(this, {
      fallbackAggregatorId: observable,
      fallbackModeValue: observable,
      totalAggregators: computed,
      enabledAggregators: computed,
      activeFallbackAggregatorId: computed,
      activeFallbackAggregator: computed,
      fallbackMode: computed,
      setActiveFallbackAggregator: action,
      setFallbackMode: action,
    })
  }

  // Find aggregator by ID
  find(id: string): SwapAggregatorStore | undefined {
    return this.aggregators.get(id)
  }

  // Add aggregator to repository
  add(aggregator: SwapAggregatorStore): void {
    const existing = this.find(aggregator.id)
    if (existing) {
      return
    }

    this.aggregators.set(aggregator.id, aggregator)
  }

  // Get all aggregators
  getAll(): SwapAggregatorStore[] {
    return Array.from(this.aggregators.values())
  }

  // Get production ready aggregators
  getProductionReadyAggregators(): SwapAggregatorStore[] {
    return this.getAll().filter((aggregator) => aggregator.isProductionReady)
  }

  // Load from static aggregators
  loadStaticAggregators(staticAggregators: SwapAggregatorDAO[], eventBus?: IEventBus): void {
    migrateLegacyAggregatorIds()

    staticAggregators.forEach((aggregatorDAO) => {
      const aggregator = SwapAggregatorFactory.createStore(aggregatorDAO, eventBus)
      if (!this.find(aggregator.id)) {
        this.add(aggregator)
      }
    })
    this.initializeFallbackSelection()
  }

  // Computed getters
  get totalAggregators(): number {
    return this.aggregators.size
  }

  get enabledAggregators(): SwapAggregatorStore[] {
    return this.getAll().filter((aggregator) => aggregator.enabled)
  }

  get activeFallbackAggregatorId(): string {
    return this.fallbackAggregatorId
  }

  get activeFallbackAggregator(): SwapAggregatorStore | undefined {
    return this.find(this.fallbackAggregatorId)
  }

  get fallbackMode(): SwapFallbackMode {
    return this.fallbackModeValue
  }

  get productionReadyAggregators(): SwapAggregatorStore[] {
    return this.getAll().filter((aggregator) => aggregator.isProductionReady)
  }

  // Get enabled production-ready aggregators
  getEnabledProductionReadyAggregators(): SwapAggregatorStore[] {
    return this.getAll().filter((aggregator) => aggregator.enabled && aggregator.isProductionReady)
  }

  setActiveFallbackAggregator(id: string): void {
    const selectedAggregator = this.find(id)
    if (!selectedAggregator || !selectedAggregator.isProductionReady) {
      return
    }

    this.fallbackAggregatorId = id
    localStorage.setItem(FALLBACK_AGGREGATOR_KEY, id)
    this.applyFallbackSelection()
  }

  setFallbackMode(mode: SwapFallbackMode): void {
    this.fallbackModeValue = mode
    localStorage.setItem(FALLBACK_MODE_KEY, mode)
    this.applyFallbackSelection()
  }

  private initializeFallbackSelection(): void {
    const savedFallbackMode = localStorage.getItem(FALLBACK_MODE_KEY)
    if (savedFallbackMode === "single" || savedFallbackMode === "multi") {
      this.fallbackModeValue = savedFallbackMode
    }

    const savedFallbackId = localStorage.getItem(FALLBACK_AGGREGATOR_KEY)
    const firstEnabled = this.getEnabledProductionReadyAggregators()[0]?.id
    const preferredFallbackId = savedFallbackId ?? firstEnabled ?? DEFAULT_FALLBACK_AGGREGATOR_ID
    const fallbackExists = this.find(preferredFallbackId)?.isProductionReady
    const fallbackId = fallbackExists ? preferredFallbackId : this.getProductionReadyAggregators()[0]?.id

    if (!fallbackId) {
      return
    }

    this.setActiveFallbackAggregator(fallbackId)
  }

  private applyFallbackSelection(): void {
    this.getProductionReadyAggregators().forEach((aggregator) => {
      aggregator.setEnabled(this.fallbackModeValue === "multi" || aggregator.id === this.fallbackAggregatorId)
    })
  }
}
